export type YaGoalKey =
  | 'submitApplication'
  | 'register'
  | 'becomeResident'
  | 'watchVideo'
  | 'lk';

declare global {
  interface Window {
    yaCounterId?: number | string;
    yaGoals?: Partial<Record<YaGoalKey, boolean>>;
    ym?: (counterId: number, method: string, ...args: unknown[]) => void;
  }
}

const GOAL_IDS: Record<YaGoalKey, string> = {
  submitApplication: 'submit_application',
  register: 'register',
  becomeResident: 'become_resident',
  watchVideo: 'watch_video',
  lk: 'lk',
};

const BACKGROUND_VIDEO_SELECTORS = ['.home-advantages__video'];

function getCounterId(): number | null {
  const raw = window.yaCounterId;
  if (raw === undefined || raw === null || raw === '') {
    return null;
  }
  const id = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function trackYaGoal(key: YaGoalKey): void {
  const counterId = getCounterId();
  if (!counterId || !window.ym || !window.yaGoals?.[key]) {
    return;
  }

  try {
    window.ym(counterId, 'reachGoal', GOAL_IDS[key]);
  } catch {
    // ignore metrika runtime errors
  }
}

function isBackgroundVideo(video: HTMLVideoElement): boolean {
  return BACKGROUND_VIDEO_SELECTORS.some((selector) => video.matches(selector));
}

let videoTrackingInitialized = false;
let lkLinkTrackingInitialized = false;

/** Глобальный трекинг play у пользовательских видео (не фоновых). */
export function initYandexMetrikaVideoTracking(): void {
  if (videoTrackingInitialized) {
    return;
  }
  videoTrackingInitialized = true;

  document.addEventListener(
    'play',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLVideoElement)) {
        return;
      }
      if (isBackgroundVideo(target) || target.dataset.yaVideoGoalSent === '1') {
        return;
      }

      target.dataset.yaVideoGoalSent = '1';
      trackYaGoal('watchVideo');
    },
    true,
  );
}

/** Клики по ссылкам перехода в ЛК. */
export function initYandexMetrikaLkLinkTracking(): void {
  if (lkLinkTrackingInitialized) {
    return;
  }
  lkLinkTrackingInitialized = true;

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    const link = target?.closest<HTMLAnchorElement>('a[href]');
    if (!link) {
      return;
    }

    try {
      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin) {
        return;
      }
      if (!/^\/lk(\/|$)/i.test(url.pathname)) {
        return;
      }
    } catch {
      return;
    }

    trackYaGoal('lk');
  });
}
